import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  ReactNode,
} from "react";
import * as signalR from "@microsoft/signalr";
import EntraIdContext from "contexts/EntraIdContext"; // Import EntraID context
import appConfig from "appConfig";
import { parseOrderMessage, OrderMessageData } from "utils/orderMessageParser";
import dayjs from "dayjs";
import axios from "axios";
import { useUserInfo } from "hooks/useUserInfo"; // Get the current signed-in / overridden user
import useApi from "api/api";
import { isOpsEnvironment } from "utils/dynamicConfig";
import { appInsights } from "utils/appInsights"; // Import Application Insights

export type ConnectionStatus = "disconnected" | "connecting" | "connected" | "reconnecting";

export interface Message {
  sender: string;
  data: OrderMessageData | null;
  rawmsg: any;
  msgTime: Date;
}

interface SignalRContextValue {
  connection: signalR.HubConnection | null;
  status: ConnectionStatus;
  messages: Message[];
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>; // Expose setMessages for clearing messages
  sendMessage: (sender: string, message: any) => Promise<void>;
  connect: () => Promise<void>;
}

const SignalRContext = createContext<SignalRContextValue | undefined>(undefined);

interface SignalRProviderProps {
  children: ReactNode;
}

// ------------------------- SignalR Token Caching -------------------------
// Cache negotiated SignalR access tokens to avoid excessive negotiate calls
// while ensuring tokens are refreshed before expiry

interface CachedSignalRToken {
  accessToken: string;
  url: string;
  expiresAt: number; // Unix timestamp in milliseconds
}

let cachedSignalRToken: CachedSignalRToken | null = null;

// Cache token for 11.5 hours (API issues 12-hour tokens)
// This provides 30-minute buffer before actual expiry
const TOKEN_CACHE_DURATION_MS = 11.5 * 60 * 60 * 1000; // 11.5 hours

const isCachedTokenValid = (): boolean => {
  if (!cachedSignalRToken) return false;
  const now = Date.now();
  return now < cachedSignalRToken.expiresAt;
};

const clearCachedToken = () => {
  cachedSignalRToken = null;
};

export const SignalRProvider: React.FC<SignalRProviderProps> = ({ children }) => {
  const { getCurrentToken, accessToken, isAuthenticated } = useContext(EntraIdContext) ?? {}; // Get auth state
  const [connection, setConnection] = useState<signalR.HubConnection | null>(null);
  const [status, setStatus] = useState<ConnectionStatus>("disconnected");
  const [messages, setMessages] = useState<Message[]>([]);
  const { userInitials, userName } = useUserInfo();   
  const { get } = useApi();

  // Helper to check if SignalR debug logging is enabled
  const isDebugEnabled = () => typeof window !== 'undefined' && (window as any)._debugSignalR === true;

  // Track in-flight ownership checks to prevent duplicate simultaneous requests
  const ownershipChecksInProgress = React.useRef<Map<string, Promise<boolean>>>(new Map());
  
  // Batch ownership checks to prevent overwhelming the API during message bursts
  const ownershipCheckQueue = React.useRef<Set<string>>(new Set());
  const ownershipCheckTimer = React.useRef<NodeJS.Timeout | null>(null);
  const ownershipCheckDeferredPromises = React.useRef<Map<string, { resolve: (value: boolean) => void, reject: (reason?: any) => void }>>(new Map());
  const OWNERSHIP_CHECK_BATCH_DELAY = 300; // ms - wait time to batch multiple checks together

  // Use useCallback to ensure we always use the latest 'get' function
  // Returns: true = owned, false = not owned, throws on error (auth failure, network, etc.)
  const checkOrderOwnership = useCallback(async (orderSeqId: string): Promise<boolean> => {
    // Check if there's already a request in flight for this order
    const existingCheck = ownershipChecksInProgress.current.get(orderSeqId);
    if (existingCheck) {
      if (isDebugEnabled()) {
        console.log(`>>> SignalR: Ownership check already in progress for order ${orderSeqId}, waiting...`);
      }
      return existingCheck;
    }

    // Create new request and track it
    if (isDebugEnabled()) {
      console.log(`>>> SignalR: Starting ownership check for order ${orderSeqId}`);
    }
    const checkPromise = (async () => {
      try {
        const res = await get<boolean>(`${appConfig.api.orderApiUrl}/orders/${orderSeqId}/check-ownership`);
        
        // If API call failed (token refresh failed, etc.), res will be null
        if (res === null) {
          throw new Error('Ownership check API call failed (returned null)');
        }
        
        const isOwned = res?.data === true;
        if (isDebugEnabled()) {
          console.log(`>>> SignalR: Ownership check completed for order ${orderSeqId}:`, isOwned);
        }
        return isOwned;
      } catch (error) {
        // Don't catch errors - let them bubble up so caller can distinguish
        // between "user doesn't own it" (false) vs "couldn't verify" (error)
        // Always log errors (not behind debug flag)
        console.error(`>>> SignalR: Ownership check FAILED for order ${orderSeqId}:`, error);
        throw error; // ← Throw instead of returning false!
      } finally {
        // Remove from in-progress map
        ownershipChecksInProgress.current.delete(orderSeqId);
      }
    })();

    ownershipChecksInProgress.current.set(orderSeqId, checkPromise);
    return checkPromise;
  }, [get]); // ← Re-create when 'get' changes (which happens when useApi re-renders)

  // Throttled ownership check - batches multiple checks together to prevent API overload
  const checkOrderOwnershipThrottled = useCallback((orderSeqId: string): Promise<boolean> => {
    // Check if there's already a request in flight for this order
    const existingCheck = ownershipChecksInProgress.current.get(orderSeqId);
    if (existingCheck) {
      if (isDebugEnabled()) {
        console.log(`>>> SignalR: Ownership check already in progress for order ${orderSeqId}, reusing...`);
      }
      return existingCheck;
    }

    // Create a deferred promise that will be resolved when the batch processes
    const deferredPromise = new Promise<boolean>((resolve, reject) => {
      ownershipCheckDeferredPromises.current.set(orderSeqId, { resolve, reject });
    });

    // Track this promise
    ownershipChecksInProgress.current.set(orderSeqId, deferredPromise);

    // Add to queue
    ownershipCheckQueue.current.add(orderSeqId);

    // Clear existing timer and set a new one
    if (ownershipCheckTimer.current) {
      clearTimeout(ownershipCheckTimer.current);
    }

    ownershipCheckTimer.current = setTimeout(async () => {
      const orderSeqIdsToCheck = Array.from(ownershipCheckQueue.current);
      ownershipCheckQueue.current.clear();

      if (isDebugEnabled()) {
        console.log(`>>> SignalR: Processing batched ownership checks for ${orderSeqIdsToCheck.length} orders`);
      }

      // Process all queued checks in parallel
      const results = await Promise.allSettled(
        orderSeqIdsToCheck.map(async (id) => {
          try {
            // Remove from in-flight map temporarily so checkOrderOwnership doesn't return the deferred promise
            const deferredPromise = ownershipChecksInProgress.current.get(id);
            ownershipChecksInProgress.current.delete(id);
            
            // Now call the actual ownership check which will make the API call
            const result = await checkOrderOwnership(id);
            
            return { id, result, status: 'success' as const };
          } catch (error) {
            return { id, error, status: 'error' as const };
          }
        })
      );

      // Resolve/reject all deferred promises
      results.forEach((promiseResult) => {
        if (promiseResult.status === 'fulfilled') {
          const { id, result: owned, status } = promiseResult.value;
          const deferred = ownershipCheckDeferredPromises.current.get(id);
          
          if (deferred) {
            if (status === 'success') {
              // Resolve with the ownership result
              deferred.resolve(owned);
            } else {
              // Reject with the error
              deferred.reject((promiseResult.value as any).error);
            }
            ownershipCheckDeferredPromises.current.delete(id);
          }
        } else {
          // This shouldn't happen as we're catching errors in the map, but handle it anyway
          const id = orderSeqIdsToCheck[results.indexOf(promiseResult)];
          console.error(`>>> SignalR: Unexpected error in batch ownership check for ${id}:`, promiseResult.reason);
          const deferred = ownershipCheckDeferredPromises.current.get(id);
          if (deferred) {
            deferred.reject(promiseResult.reason);
            ownershipCheckDeferredPromises.current.delete(id);
          }
        }
      });
    }, OWNERSHIP_CHECK_BATCH_DELAY);

    return deferredPromise;
  }, [checkOrderOwnership]);

  // Store the latest checkOrderOwnership function in a ref so message handlers can always use the fresh version
  // without needing to restart the SignalR connection
  const checkOrderOwnershipRef = React.useRef(checkOrderOwnershipThrottled);
  React.useEffect(() => {
    checkOrderOwnershipRef.current = checkOrderOwnershipThrottled;
  }, [checkOrderOwnershipThrottled]);

  // **Load Messages from Session Storage on Component Mount**
  useEffect(() => {
    const MESSAGES_STORAGE_KEY = `signalr_messages_${userInitials}`;
    const storedMessages = sessionStorage.getItem(MESSAGES_STORAGE_KEY);
    if (!storedMessages) return;
  
    try {
      const parsedMessages: Message[] = JSON.parse(storedMessages);
  
      // Ensure `msgTime` is converted back to a `Date` object
      const restoredMessages = parsedMessages.map((msg) => ({
        ...msg,
        msgTime: new Date(msg.msgTime), // Convert stored string back to Date
      }));

      setMessages(restoredMessages);
    } catch (error) {
      console.error("Error parsing messages from sessionStorage:", error);
      setMessages([]);
    }
  }, [userInitials]);

  // **Save Messages to Session Storage Whenever Messages Change**
  useEffect(() => {
    if (!userInitials) return; // Ensure userInitials is available

    const MESSAGES_STORAGE_KEY = `signalr_messages_${userInitials}`;
    sessionStorage.setItem(MESSAGES_STORAGE_KEY, JSON.stringify(messages));
  }, [messages, userInitials]);

  // **Negotiate with SignalR endpoint to get access token and URL**
  // Uses caching to avoid excessive negotiate calls
  const negotiateSignalR = useCallback(async (): Promise<{ url: string; accessToken: string } | null> => {
    try {
      if (!getCurrentToken) {
        console.warn("SignalR: No token function available for negotiate");
        return null;
      }

      // Check if we have a valid cached token
      if (isCachedTokenValid()) {
        if (isDebugEnabled()) {
          const remainingHours = ((cachedSignalRToken!.expiresAt - Date.now()) / (60 * 60 * 1000)).toFixed(1);
          console.log(">>> SignalR: Using cached token (valid for", remainingHours, "more hours)");
        }
        return {
          url: cachedSignalRToken!.url,
          accessToken: cachedSignalRToken!.accessToken
        };
      }

      // Need to negotiate for fresh token
      console.log(">>> SignalR: Negotiating for fresh access token...");
      const startTime = Date.now();
      
      const entraIdToken = getCurrentToken();
      if (!entraIdToken) {
        console.warn("SignalR: No EntraID token available for negotiate");
        return null;
      }

      const response = await fetch(appConfig.api.negotiateUrlSignalR, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${entraIdToken}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`Negotiate endpoint returned ${response.status}: ${response.statusText}`);
      }

      const negotiateData = await response.json();
      const duration = Date.now() - startTime;

      // Cache the new token
      cachedSignalRToken = {
        url: negotiateData.data.url,
        accessToken: negotiateData.data.accessToken,
        expiresAt: Date.now() + TOKEN_CACHE_DURATION_MS
      };

      console.log(`>>> SignalR: Token negotiated successfully in ${duration}ms (valid for 11.5 hours)`);
      
      // Track successful token refresh in Application Insights
      appInsights?.trackEvent({
        name: 'SignalR_TokenNegotiated',
        properties: {
          durationMs: duration,
          userName: userName,
          userInitials: userInitials,
          timestamp: new Date().toISOString()
        }
      });

      return {
        url: negotiateData.data.url,
        accessToken: negotiateData.data.accessToken
      };

    } catch (error) {
      console.error("SignalR: Negotiate failed:", error);
      
      // Track negotiate failure in Application Insights
      appInsights?.trackException({
        exception: error instanceof Error ? error : new Error(String(error)),
        properties: {
          context: 'SignalR_NegotiateFailed',
          userName: userName,
          userInitials: userInitials,
          timestamp: new Date().toISOString()
        }
      });

      return null;
    }
  }, [getCurrentToken, userName, userInitials]);

  const startConnection = useCallback(async () => {
    // Use getCurrentToken() instead of accessToken to always get latest token from ref
    // This avoids needing accessToken in dependency array (prevents re-renders on keep-alive refresh)
    if (!getCurrentToken || !isAuthenticated) {
      console.warn("SignalR connection not started: No token function or not authenticated.");
      return;
    }
    
    const currentToken = getCurrentToken();
    if (!currentToken) {
      console.warn("SignalR connection not started: No access token available.");
      return;
    }

    setStatus("connecting");
    const connectionStartTime = Date.now();
    
    try {
      console.log(">>> Starting SignalR connection...");
      
      // Get initial negotiated token
      const initialNegotiate = await negotiateSignalR();
      if (!initialNegotiate) {
        throw new Error("Failed to negotiate SignalR token");
      }

      // Custom retry policy with infinite retries and exponential backoff
      const retryDelays = [0, 2000, 10000, 30000, 60000]; // 0s, 2s, 10s, 30s, 1min
      const maxRetryDelay = 5 * 60 * 1000; // Cap at 5 minutes
      
      const customRetryPolicy: signalR.IRetryPolicy = {
        nextRetryDelayInMilliseconds: (retryContext: signalR.RetryContext) => {
          // Use predefined delays for first attempts, then exponential backoff
          if (retryContext.previousRetryCount < retryDelays.length) {
            const delay = retryDelays[retryContext.previousRetryCount];
            console.log(`>>> SignalR: Retry attempt ${retryContext.previousRetryCount + 1} in ${delay}ms`);
            return delay;
          }
          
          // After initial attempts, use exponential backoff capped at 5 minutes
          const exponentialDelay = Math.pow(2, retryContext.previousRetryCount - retryDelays.length + 5) * 1000;
          const delay = Math.min(maxRetryDelay, exponentialDelay);
          console.log(`>>> SignalR: Retry attempt ${retryContext.previousRetryCount + 1} in ${delay}ms (${Math.round(delay/1000)}s)`);
          
          // Track retry attempts in Application Insights (sample to avoid excessive telemetry)
          // Only log every 5th attempt after the initial attempts
          if (retryContext.previousRetryCount >= retryDelays.length && 
              (retryContext.previousRetryCount - retryDelays.length) % 5 === 0) {
            appInsights?.trackEvent({
              name: 'SignalR_ExtendedRetryAttempt',
              properties: {
                attemptNumber: retryContext.previousRetryCount + 1,
                delayMs: delay,
                elapsedTimeMs: retryContext.elapsedMilliseconds,
                userName: userName,
                userInitials: userInitials,
                timestamp: new Date().toISOString()
              }
            });
          }
          
          return delay; // Never return null - retry forever
        }
      };

      const hubConnection = new signalR.HubConnectionBuilder()
        .withUrl(initialNegotiate.url, {
          // Dynamic token factory that refreshes token when needed
          accessTokenFactory: async () => {
            if (isDebugEnabled()) {
              console.log(">>> SignalR: accessTokenFactory called");
            }
            
            // Check if cached token is still valid
            if (isCachedTokenValid()) {
              if (isDebugEnabled()) {
                console.log(">>> SignalR: Returning cached token");
              }
              return cachedSignalRToken!.accessToken;
            }
            
            // Token expired or not available, re-negotiate
            console.log(">>> SignalR: Token expired, re-negotiating...");
            const freshNegotiate = await negotiateSignalR();
            
            if (!freshNegotiate) {
              console.error(">>> SignalR: Failed to re-negotiate token in accessTokenFactory");
              // Return empty string rather than throw - SignalR will handle the auth failure
              return "";
            }
            
            console.log(">>> SignalR: Fresh token obtained for reconnection");
            return freshNegotiate.accessToken;
          },
        })
        .withAutomaticReconnect(customRetryPolicy)
        // .configureLogging(signalR.LogLevel.Information)
        .configureLogging(signalR.LogLevel.Error)
        .build();

      hubConnection.on("broadcastMessage", async (sender: string, data: any) => {
        if (isDebugEnabled()) {
          console.log(`>>> SignalR: Message received for order`, { sender, timestamp: new Date().toISOString() });
        }
        
        const parsedMessage: OrderMessageData | null = parseOrderMessage(data);
        if (!parsedMessage) return;

        const orderSeqId = parsedMessage.orderSeqId;
        
        // **🚫 Skip processing if in OPS mode - orders are not relevant for ops view**
        if (isOpsEnvironment()) {
          if (isDebugEnabled()) {
            console.log(`>>> SignalR: Skipping message for order ${orderSeqId} - running in OPS mode`);
          }
          return;
        }
        
        if (isDebugEnabled()) {
          console.log(`>>> SignalR: Processing message for order ${orderSeqId}`);
        }

        // **1️⃣ Check if order is in the ignore cache**
        if (isOrderInIgnoreCache(orderSeqId, userInitials!)) {
          if (isDebugEnabled()) {
            console.log(`>>> SignalR: Order ${orderSeqId} is in ignore cache, skipping`);
          }
          return;
        }

        // **2️⃣ Check if order is already confirmed as owned**
        if (isOrderInOwnedCache(orderSeqId, userInitials!)) {
          if (isDebugEnabled()) {
            console.log(`>>> SignalR: Order ${orderSeqId} is in owned cache, allowing message`);
          }
        } else {
          // **3️⃣ Call API if not found in either cache**
          if (isDebugEnabled()) {
            console.log(`>>> SignalR: Order ${orderSeqId} not in cache, checking ownership via API`);
          }
          
          try {
            // Use ref to always get the latest version of checkOrderOwnership with fresh 'get' function
            const belongsToUser = await checkOrderOwnershipRef.current(orderSeqId);
            
            if (belongsToUser) {
              if (isDebugEnabled()) {
                console.log(`>>> SignalR: Order ${orderSeqId} belongs to user, caching`);
              }
              addOrderToOwnedCache(orderSeqId, userInitials!);
            } else {
              if (isDebugEnabled()) {
                console.log(`>>> SignalR: Order ${orderSeqId} does not belong to user, ignoring and caching`);
              }
              addOrderToIgnoreCache(orderSeqId, userInitials!);
              return;
            }
          } catch (error) {
            // Ownership check failed (auth error, network error, etc.)
            // Don't cache as ignored - we simply couldn't verify
            // Always show warnings (not behind debug flag)
            console.warn(`>>> SignalR: Could not verify ownership for order ${orderSeqId}, skipping message (not caching)`, error);
            return; // Skip this message, but don't cache as ignored
          }
        }

        // **4️⃣ Check for duplicate raw messages**
        if (messages.some((msg) => msg.rawmsg === data)) {
          //console.warn("Ignoring duplicate message:", data);
          return;
        }

        setMessages((prevMessages) => [
            ...prevMessages,
            {
              sender,
              data: parsedMessage,
              rawmsg: data,
              msgTime: new Date(),
            },
          ]
        );
      });

      hubConnection.onclose(async (error) => {
        console.warn("SignalR connection closed.", error);
        const disconnectTime = Date.now();
        const connectionDuration = disconnectTime - connectionStartTime;
        
        setStatus("disconnected");
        
        // Track disconnection in Application Insights
        appInsights?.trackEvent({
          name: 'SignalR_ConnectionClosed',
          properties: {
            errorMessage: error?.message || 'No error',
            errorType: error?.name || 'Unknown',
            connectionDurationMs: connectionDuration,
            connectionDurationMinutes: Math.round(connectionDuration / 60000),
            userName: userName,
            userInitials: userInitials,
            timestamp: new Date().toISOString()
          }
        });
        
        // Enhanced recovery logic: If connection closed (likely after retry exhaustion),
        // attempt a full reconnection with fresh token after a delay
        // This acts as a safety net for scenarios where automatic reconnect gives up
        console.log(">>> SignalR: Connection closed, will attempt full reconnection in 60 seconds...");
        
        setTimeout(async () => {
          // Only attempt reconnection if still disconnected and authenticated
          if (status === "disconnected" && isAuthenticated && accessToken) {
            console.log(">>> SignalR: Attempting full reconnection with fresh token...");
            
            // Clear cached token to force fresh negotiate
            clearCachedToken();
            
            // Track recovery attempt
            appInsights?.trackEvent({
              name: 'SignalR_RecoveryAttempt',
              properties: {
                reason: 'onclose_handler',
                minutesSinceDisconnect: 1,
                userName: userName,
                userInitials: userInitials,
                timestamp: new Date().toISOString()
              }
            });
            
            try {
              await startConnection();
            } catch (recoveryError) {
              console.error(">>> SignalR: Recovery attempt failed:", recoveryError);
              
              appInsights?.trackException({
                exception: recoveryError instanceof Error ? recoveryError : new Error(String(recoveryError)),
                properties: {
                  context: 'SignalR_RecoveryFailed',
                  userName: userName,
                  userInitials: userInitials,
                  timestamp: new Date().toISOString()
                }
              });
            }
          }
        }, 60000); // Wait 60 seconds before attempting full reconnection
      });

      hubConnection.onreconnecting((error) => {
        console.warn("SignalR reconnecting...", error);
        setStatus("reconnecting");
        
        // Track reconnection attempts in Application Insights
        appInsights?.trackEvent({
          name: 'SignalR_Reconnecting',
          properties: {
            errorMessage: error?.message || 'No error',
            errorType: error?.name || 'Unknown',
            userName: userName,
            userInitials: userInitials,
            timestamp: new Date().toISOString()
          }
        });
      });

      hubConnection.onreconnected(async (connectionId) => {
        const reconnectTime = Date.now();
        const downtime = reconnectTime - connectionStartTime;
        
        console.info("SignalR reconnected. Connection ID:", connectionId);
        
        // Track successful reconnection in Application Insights
        appInsights?.trackEvent({
          name: 'SignalR_Reconnected',
          properties: {
            connectionId: connectionId,
            downtimeMs: downtime,
            downtimeMinutes: Math.round(downtime / 60000),
            userName: userName,
            userInitials: userInitials,
            timestamp: new Date().toISOString()
          }
        });
        
        // Re-add user to their username group after reconnection
        if (userName) {
          try {
            await hubConnection.invoke('IdentifyConnection', userName, 'web');
            console.info(`SignalR: User ${userName} re-added to username group after reconnect`);
          } catch (error) {
            console.error('SignalR: Failed to re-identify connection after reconnect:', error);
            
            appInsights?.trackException({
              exception: error instanceof Error ? error : new Error(String(error)),
              properties: {
                context: 'SignalR_ReidentifyFailed',
                userName: userName,
                userInitials: userInitials,
                timestamp: new Date().toISOString()
              }
            });
          }
        }
        
        setStatus("connected");
      });

      await hubConnection.start();
      const connectionEstablishedTime = Date.now();
      const totalConnectionTime = connectionEstablishedTime - connectionStartTime;
      
      console.info(`SignalR connected successfully in ${totalConnectionTime}ms.`);
      
      // Track successful connection in Application Insights
      appInsights?.trackEvent({
        name: 'SignalR_Connected',
        properties: {
          connectionTimeMs: totalConnectionTime,
          userName: userName,
          userInitials: userInitials,
          timestamp: new Date().toISOString()
        }
      });
      
      // Add user to their username group for targeted messages
      if (userName) {
        try {
          await hubConnection.invoke('IdentifyConnection', userName, 'web');
          console.info(`SignalR: User ${userName} added to username group`);
        } catch (error) {
          console.error('SignalR: Failed to identify connection:', error);
          
          appInsights?.trackException({
            exception: error instanceof Error ? error : new Error(String(error)),
            properties: {
              context: 'SignalR_IdentifyConnectionFailed',
              userName: userName,
              userInitials: userInitials,
              timestamp: new Date().toISOString()
            }
          });
        }
      }
      
      setStatus("connected");
      setConnection(hubConnection);
    } catch (error) {
      console.error("Error establishing SignalR connection:", error);
      setStatus("disconnected");
      
      // Track connection failure in Application Insights
      appInsights?.trackException({
        exception: error instanceof Error ? error : new Error(String(error)),
        properties: {
          context: 'SignalR_ConnectionFailed',
          userName: userName,
          userInitials: userInitials,
          timestamp: new Date().toISOString()
        }
      });
      
      // Clear cached token on connection failure to force fresh negotiate on next attempt
      clearCachedToken();
    }
  }, [isAuthenticated, getCurrentToken, userInitials, userName, negotiateSignalR]);
  // Note: 
  // - Includes negotiateSignalR for token refresh capability
  // - Does NOT include accessToken to avoid re-creating startConnection every 15min (keep-alive refresh)
  // - Token is accessed via getCurrentToken() ref in negotiateSignalR, so always gets latest token
  // - Message handlers use checkOrderOwnershipRef.current to always get the latest function with fresh 'get'

  useEffect(() => {
    // Only start a new connection if we have no connection yet
    if (!connection && accessToken && isAuthenticated) {
      if (!getCurrentToken) {
        console.warn("No token function. Skipping connect.");
        return;
      }
      let token = getCurrentToken();      
      if(token && token !== null && token !== undefined) {
        console.log(">>> Starting SignalR connection...");
        startConnection();
      }
    }

    // Don't stop/restart on every token change; only clean up if unmounting.
    return () => {
      // If you do want to stop the connection later, do it conditionally here.
    };
  }, [connection, accessToken, isAuthenticated, startConnection]);

  return (
    <SignalRContext.Provider value={{ 
      connection, 
      status, 
      messages, 
      setMessages, 
      sendMessage: async () => {},
      connect: async () => {
        // When user manually triggers reconnect, clear cached token to force fresh negotiate
        console.log(">>> SignalR: Manual reconnect triggered by user");
        clearCachedToken();
        
        appInsights?.trackEvent({
          name: 'SignalR_ManualReconnect',
          properties: {
            userName: userName,
            userInitials: userInitials,
            timestamp: new Date().toISOString()
          }
        });
        
        await startConnection();
      }
    }}>
      {children}
    </SignalRContext.Provider>
  );
};

export const useSignalR = (): SignalRContextValue => {
  const context = useContext(SignalRContext);
  if (context === undefined) {
    throw new Error("useSignalR must be used within a SignalRProvider");
  }
  return context;
};

// ------------------------- Local Storage Caching -------------------------

const OWNED_CACHE_KEY = "owned_orders_cache";
const IGNORE_CACHE_KEY = "ignored_orders_cache";
const CACHE_EXPIRATION_DAYS = 7;

/**
 * Load cached OrderSeqId values from localStorage.
 */
const loadCache = (cacheKey: string, userInitials: string): string[] => {
  const data = localStorage.getItem(`${cacheKey}_${userInitials}`);
  if (!data) return [];
  const { orders, expiry } = JSON.parse(data);

  if (new Date().getTime() > expiry) {
    localStorage.removeItem(`${cacheKey}_${userInitials}`);
    return [];
  }

  return orders || [];
};

const isOrderInOwnedCache = (orderSeqId: string, userInitials: string): boolean =>
  loadCache(OWNED_CACHE_KEY, userInitials).includes(orderSeqId);

const isOrderInIgnoreCache = (orderSeqId: string, userInitials: string): boolean =>
  loadCache(IGNORE_CACHE_KEY, userInitials).includes(orderSeqId);

const updateCache = (cacheKey: string, orderSeqId: string, userInitials: string) => {
  const existingOrders = loadCache(cacheKey, userInitials);
  const newOrders = [...new Set([...existingOrders, orderSeqId])];
  const expiry = new Date().getTime() + CACHE_EXPIRATION_DAYS * 24 * 60 * 60 * 1000;

  localStorage.setItem(`${cacheKey}_${userInitials}`, JSON.stringify({ orders: newOrders, expiry }));
};

const addOrderToOwnedCache = (orderSeqId: string, userInitials: string) =>
  updateCache(OWNED_CACHE_KEY, orderSeqId, userInitials);

const addOrderToIgnoreCache = (orderSeqId: string, userInitials: string) =>
  updateCache(IGNORE_CACHE_KEY, orderSeqId, userInitials);